import React, { useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { unbindCrossEticket } from 'features/authorization/services';
import { ICrossWithServiceInfo } from 'features/transaction-details/types';
import { TransactionViewContext } from 'features/transaction-details/types/transactionCrossContext';
import { formatTransactionAmount } from 'features/transaction-details/helpers';
import { getServiceByETicket } from 'features/transaction-details/services';
import { getCrossOperatorName } from 'features/authorization/helpers';
import { extractErrorMessage } from 'features/quotation/helpers';

type UnbindModalStep = 'confirm' | 'success';

interface UnbindCrossConfirmModalProps {
	open: boolean;
	cross: ICrossWithServiceInfo | null;
	authorizationId: number;
	transactionContext: TransactionViewContext;
	operators: { value: string; description: string }[];
	onClose: () => void;
	/** Actualiza la transacción en segundo plano; el modal permanece en el paso de éxito hasta que el usuario cierre. */
	onSuccess: () => void;
	onRequestRefund: (authorizationId: number) => void;
	onRequestFinalize: () => void;
}

export const UnbindCrossConfirmModal = ({
	open,
	cross,
	authorizationId,
	transactionContext,
	operators,
	onClose,
	onSuccess,
	onRequestRefund,
	onRequestFinalize,
}: UnbindCrossConfirmModalProps): JSX.Element => {
	const [step, setStep] = useState<UnbindModalStep>('confirm');
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [pnr, setPnr] = useState<string | null>(null);
	const [ticketDealId, setTicketDealId] = useState<number | null>(null);
	const [unboundAmountLabel, setUnboundAmountLabel] = useState<string>('');

	useEffect(() => {
		if (!open) {
			setStep('confirm');
			setPnr(null);
			setTicketDealId(null);
			setError(null);
			setUnboundAmountLabel('');
			return;
		}

		if (!cross?.eticket) return;

		const load = async (): Promise<void> => {
			setLoading(true);
			try {
				const response = await getServiceByETicket(cross.eticket);
				const info = response.data.elements[0];
				if (info) {
					setPnr(info.serviceReference);
					setTicketDealId(info.saleOrderDealId);
				}
			} catch {
				// optional enrichment
			} finally {
				setLoading(false);
			}
		};

		void load();
	}, [open, cross?.eticket]);

	const handleClose = (): void => {
		setStep('confirm');
		onClose();
	};

	const handleConfirm = async (): Promise<void> => {
		if (!cross) return;
		setSubmitting(true);
		setError(null);
		try {
			await unbindCrossEticket({
				authorizationId,
				eticket: cross.eticket,
				amount: cross.amount,
			});
			setUnboundAmountLabel(formatTransactionAmount(cross.amount, cross.coinCode));
			setStep('success');
			onSuccess();
		} catch (err) {
			setError(extractErrorMessage(err, 'Ocurrió un error al desvincular el boleto'));
		} finally {
			setSubmitting(false);
		}
	};

	if (!cross) {
		return <></>;
	}

	const operatorName = getCrossOperatorName(operators, cross.operator);
	const amountLabel = formatTransactionAmount(cross.amount, cross.coinCode);

	if (step === 'success') {
		return (
			<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<CheckCircleIcon color="success" />
					Boleto desvinculado
				</DialogTitle>
				<DialogContent dividers>
					<Alert severity="success" sx={{ mb: 2 }}>
						El e-ticket <strong>{cross.eticket}</strong>
						{pnr ? (
							<>
								{' '}
								(PNR <strong>{pnr}</strong>)
							</>
						) : null}{' '}
						quedó desvinculado de la autorización <strong>#{authorizationId}</strong>.
					</Alert>

					<Typography variant="body2" sx={{ mb: 2 }}>
						Se liberaron <strong>{unboundAmountLabel || amountLabel}</strong> en esta autorización. El saldo quedó
						disponible para volver a cruzar o para devolver al cliente.
					</Typography>

					<Alert severity="info">
						<Typography variant="body2" component="div">
							<strong>Próximos pasos sugeridos:</strong>
							<ul style={{ margin: '8px 0 0', paddingLeft: '1.25rem' }}>
								<li>
									Si <strong>no</strong> vas a reautorizar y cruzar el mismo monto en la tarjeta de{' '}
									<strong>{transactionContext.cardholder}</strong>, registrá una <strong>devolución</strong>.
								</li>
								<li>
									Si el saldo pendiente con el cliente ya está resuelto, podés <strong>finalizar la transacción</strong>
									.
								</li>
								<li>En el GDS/BSP, verificá el estado del boleto para el reembolso al pasajero si corresponde.</li>
							</ul>
						</Typography>
					</Alert>
				</DialogContent>
				<DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 2, py: 1.5 }}>
					<Button onClick={handleClose}>Cerrar</Button>
					<Button
						variant="contained"
						color="error"
						startIcon={<CurrencyExchangeIcon />}
						onClick={() => onRequestRefund(authorizationId)}
					>
						Ir a devolución
					</Button>
					<Button variant="outlined" color="primary" onClick={onRequestFinalize}>
						Finalizar transacción
					</Button>
				</DialogActions>
			</Dialog>
		);
	}

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<LinkOffIcon color="warning" />
				Desvincular boleto
			</DialogTitle>
			<DialogContent dividers>
				{loading && (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
						<CircularProgress size={28} />
					</Box>
				)}

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					<Typography variant="body2">
						Vas a desvincular el e-ticket <strong>{cross.eticket}</strong>
						{pnr ? (
							<>
								{' '}
								(PNR <strong>{pnr}</strong>)
							</>
						) : null}{' '}
						de la autorización <strong>#{authorizationId}</strong> por <strong>{amountLabel}</strong>.
						{operatorName ? ` Operador: ${operatorName}.` : ''}
					</Typography>

					{ticketDealId != null && ticketDealId !== transactionContext.transactionDealId && (
						<Alert severity="info">
							El boleto pertenece al caso de venta <strong>{ticketDealId}</strong>. La transacción está en el caso{' '}
							<strong>{transactionContext.transactionDealId}</strong>.
						</Alert>
					)}

					<Alert severity="warning">
						<Typography variant="subtitle2" gutterBottom>
							Qué implica esta acción
						</Typography>
						<Typography variant="body2" component="div">
							<ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
								<li>
									El monto cruzado (<strong>{amountLabel}</strong>) volverá como saldo sin cruzar en esta autorización
									de <strong>{transactionContext.cardholder}</strong>.
								</li>
								<li>
									En el GDS/BSP el boleto puede quedar en estado que permita reembolso al pasajero según reglas de la
									aerolínea y del operador.
								</li>
								<li>
									Si no volvés a cruzar el mismo monto en esta tarjeta, deberás registrar una{' '}
									<strong>devolución</strong> en la transacción.
								</li>
								<li>
									Si no se reautoriza en la misma tarjeta, conviene <strong>finalizar la transacción</strong> para
									cerrar el saldo pendiente con el cliente.
								</li>
							</ul>
						</Typography>
					</Alert>

					{error && <Alert severity="error">{error}</Alert>}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={submitting}>
					Cancelar
				</Button>
				<Button
					variant="contained"
					color="warning"
					onClick={() => void handleConfirm()}
					disabled={submitting || loading}
					startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <LinkOffIcon />}
				>
					Confirmar desvinculación
				</Button>
			</DialogActions>
		</Dialog>
	);
};
